import {
  createWorkflowCriterion,
  isFieldPersonSource,
  WorkflowApprovalConfig,
  WorkflowApprovalModel,
  WorkflowCancelConfig,
  WorkflowCanvas,
  WorkflowEmailConfig,
  WorkflowEmailRecipientSource,
  WorkflowConditionCriterion,
  WorkflowConditionOperator,
  WorkflowConditionPath,
  WorkflowFieldSettings,
  WorkflowFieldVisibility,
  WorkflowFormField,
  WorkflowPauseConfig,
  WorkflowPauseUnit,
  WorkflowPersonSource,
  WorkflowServerJson,
  WorkflowTask,
} from "./workflowBuilderData";

/**
 * The JSON columns of a workflow graph — a stage's ConfigJson, a field's SettingsJson and a path
 * transition's ConditionJson — written in the shapes the API reads (Epic 80372's Validate
 * checklist, the 80421/80423/80424 stage handlers). Used by workflowGraphMapper.ts.
 */

export const TASK_ID_PREFIX = "wf-";

// ----- vocabulary -----

/** UI sources are kebab-case ("workflow-creator"); the runtime reads camelCase ("workflowCreator").
 *  A mechanical, reversible mapping keeps the two in step without a lookup table per editor. */
export const kebabToCamel = (value: string) =>
  value.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
export const camelToKebab = (value: string) =>
  value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

/** PersonResolver's source names (WorkflowPersonSources) — the canvas OwnerRule and approver. */
const PERSON_SOURCE_TO_RESOLVER: Record<WorkflowPersonSource, string> = {
  "specific-user": "user",
  creator: "creator",
  "creator-manager": "creatorManager",
  "field-user": "fieldUser",
  "field-user-manager": "fieldUserManager",
};

/** TaskStageHandler's assignModel names — the same set, except a named user is "specificUser". */
const PERSON_SOURCE_TO_ASSIGN_MODEL: Record<WorkflowPersonSource, string> = {
  ...PERSON_SOURCE_TO_RESOLVER,
  "specific-user": "specificUser",
};

const invert = (map: Record<WorkflowPersonSource, string>) =>
  Object.fromEntries(Object.entries(map).map(([source, name]) => [name, source])) as Record<
    string,
    WorkflowPersonSource
  >;
const RESOLVER_TO_PERSON_SOURCE = invert(PERSON_SOURCE_TO_RESOLVER);
const ASSIGN_MODEL_TO_PERSON_SOURCE = invert(PERSON_SOURCE_TO_ASSIGN_MODEL);

export const toResolverSource = (source: WorkflowPersonSource) => PERSON_SOURCE_TO_RESOLVER[source];
export const fromResolverSource = (name: unknown): WorkflowPersonSource | undefined =>
  typeof name === "string" ? RESOLVER_TO_PERSON_SOURCE[name.trim()] : undefined;
export const fromAssignModel = (name: unknown): WorkflowPersonSource | undefined =>
  typeof name === "string" ? ASSIGN_MODEL_TO_PERSON_SOURCE[name.trim()] : undefined;

const APPROVAL_MODELS: ReadonlySet<string> = new Set<WorkflowApprovalModel>([
  "single",
  "all",
  "any-n",
  "percentage",
  "first",
]);

/** Accepts the pre-v1 "any" (any one approver) as what it meant: the first response decides. */
export const approvalModelFromConfig = (model: unknown): WorkflowApprovalModel | undefined => {
  if (model === "any") return "first";
  return typeof model === "string" && APPROVAL_MODELS.has(model)
    ? (model as WorkflowApprovalModel)
    : undefined;
};

// ----- approval (Epic 80422 WorkflowApprovalConfigDto) -----

/** countingModel: how votes are counted. */
const APPROVAL_MODEL_TO_API: Record<WorkflowApprovalModel, string> = {
  single: "single",
  all: "all",
  "any-n": "anyN",
  percentage: "percent",
  first: "first",
};
const API_TO_APPROVAL_MODEL = Object.fromEntries(
  Object.entries(APPROVAL_MODEL_TO_API).map(([model, name]) => [name, model]),
) as Record<string, WorkflowApprovalModel>;

/** approverSource: who approves. A named set of users is "specific" here (the resolver says "user"). */
const APPROVER_SOURCE_TO_API: Record<WorkflowPersonSource, string> = {
  ...PERSON_SOURCE_TO_RESOLVER,
  "specific-user": "specific",
};
const API_TO_APPROVER_SOURCE = invert(APPROVER_SOURCE_TO_API);

/**
 * False for an approverSource the Builder has no option for — e.g. "setting" (Epic 80431: the PIF's
 * HR / Executive Director read their PostIF setting at run time). Loading one falls back to the
 * default source, so saving would silently replace the approver. An absent source is a draft the
 * Builder itself writes, so it counts as editable.
 */
export const isBuilderEditableApproverSource = (configJson: string | null | undefined): boolean => {
  const source = asString(parseJsonObject(configJson).approverSource);
  return !source || source in API_TO_APPROVER_SOURCE;
};

const parseUserIds = (value: string) =>
  value
    .split(",")
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

/** WorkflowConditionEvaluator's `cmp` values. */
const OPERATOR_TO_CMP: Record<WorkflowConditionOperator, string> = {
  is: "eq",
  "is-not": "ne",
  contains: "contains",
  "greater-than": "gt",
  "less-than": "lt",
  "is-empty": "isnull",
};

export const CMP_TO_OPERATOR: Record<string, WorkflowConditionOperator> = Object.fromEntries(
  Object.entries(OPERATOR_TO_CMP).map(([operator, cmp]) => [cmp, operator]),
) as Record<string, WorkflowConditionOperator>;

/** PauseStageHandler accepts only these spellings — "working-days" is a runtime config error. */
const PAUSE_UNIT_TO_API: Record<WorkflowPauseUnit, string> = {
  hours: "Hours",
  days: "Days",
  "working-days": "WorkingDays",
};

/** A missing or unrecognised unit loads as "" (not set) rather than a guess: guessing would change
 *  how long the pause lasts on the next save. Validate flags it so the author chooses. */
export const pauseUnitFromApi = (unit: unknown): WorkflowPauseConfig["unit"] => {
  const normalized = typeof unit === "string" ? unit.replace(/[\s_-]/g, "").toLowerCase() : "";
  if (normalized === "hours") return "hours";
  if (normalized === "days") return "days";
  if (normalized === "workingdays") return "working-days";
  return "";
};

/** v1 VisibilityRule values. */
export const VISIBILITY_TO_API: Record<WorkflowFieldVisibility, string> = {
  show: "Show",
  hide: "Hide",
};

// ----- JSON helpers -----

export type JsonObject = Record<string, unknown>;

/** Lenient on purpose: a malformed column loads as defaults instead of failing the whole graph —
 *  the server's CONFIG_JSON_INVALID / FIELD_SETTINGS_INVALID checks are what report it. */
export const parseJsonObject = (json: string | null | undefined): JsonObject => {
  if (!json) return {};
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as JsonObject) : {};
  } catch {
    return {};
  }
};

export const asString = (value: unknown, fallback = "") =>
  typeof value === "string" ? value : fallback;
export const asNumberOrNull = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
export const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
export const asObject = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {};

const withoutEmpty = (value: JsonObject): JsonObject =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== null && entry !== ""));

/** Both sides come from JSON.stringify of the same writer, so their key order matches. */
const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What to save for a loaded row's JSON column: the server's own JSON, with only the keys the author
 * actually changed replaced. A top-level key the Builder writes counts as changed when it differs
 * from what the Builder would have written for the row as loaded (`baseline`); an unchanged key keeps
 * the server's value verbatim, which may hold more than the Builder can show (all `to` addresses,
 * an approver's `resolveTo`). A key the Builder wrote at load but no longer writes was cleared by
 * the author and is removed. Keys the Builder never writes (a web-service `bodyTemplate`) are kept.
 */
export const mergeOverServerJson = (
  written: JsonObject,
  server: WorkflowServerJson | undefined,
): JsonObject => {
  if (!server) return written;
  const merged: JsonObject = { ...server.raw };
  Object.keys(server.baseline).forEach((key) => {
    if (!(key in written)) delete merged[key];
  });
  Object.entries(written).forEach(([key, value]) => {
    const untouched = key in server.raw && sameJson(value, server.baseline[key]);
    if (!untouched) merged[key] = value;
  });
  return merged;
};

// ----- field SettingsJson -----

/** SettingsJson is type-specific: chars for text, size + accept list for file, bounds for number.
 *  The UI keeps every value on every type (so switching back doesn't lose them) — only the ones
 *  that apply to the current type are sent. Shape per WorkflowGraphValidator:
 *  `{"maxSizeMb":..,"accept":["pdf","jpg",...]}`. */
export const toSettingsJson = (field: WorkflowFormField): string | null => {
  // A loaded field keeps the SettingsJson keys the Builder doesn't edit (e.g. isMasked).
  const merged = mergeOverServerJson(settingsShape(field), field.serverSettings);
  return Object.keys(merged).length > 0 ? JSON.stringify(merged) : null;
};

/** The keys the Builder itself writes for a field's settings — also its load-time baseline. */
export const settingsShape = (field: WorkflowFormField): JsonObject => {
  const { settings } = field;
  let shape: JsonObject = {};
  if (field.dataType === "text" || field.dataType === "richtext") {
    shape = {
      minChars: settings.min,
      maxChars: settings.max,
      rows: field.dataType === "richtext" ? settings.rows : null,
    };
  } else if (field.dataType === "number") {
    shape = { min: settings.min, max: settings.max };
  } else if (field.dataType === "file") {
    const accept = settings.accept
      .split(",")
      .map((entry) => entry.trim().replace(/^\./, "").toLowerCase())
      .filter(Boolean);
    shape = { maxSizeMb: settings.maxSize, accept: accept.length > 0 ? accept : null };
  }
  return withoutEmpty(shape);
};

export const fromSettingsJson = (json: string | null): WorkflowFieldSettings => {
  const parsed = parseJsonObject(json);
  return {
    min: asNumberOrNull(parsed.minChars) ?? asNumberOrNull(parsed.min),
    max: asNumberOrNull(parsed.maxChars) ?? asNumberOrNull(parsed.max),
    rows: asNumberOrNull(parsed.rows),
    maxSize: asNumberOrNull(parsed.maxSizeMb),
    accept: asArray(parsed.accept)
      .filter((entry) => typeof entry === "string")
      .join(", "),
  };
};

// ----- task keys -----

const taskKeyOf = (task: WorkflowTask) =>
  task.id.startsWith(TASK_ID_PREFIX) ? task.id.slice(TASK_ID_PREFIX.length) : task.id;

/** taskKey must be unique within its canvas. A task added this session can collide with a loaded
 *  one because both id sequences start at 1, so collisions get a suffix. */
const assignTaskKeys = (tasks: WorkflowTask[]): string[] => {
  const used = new Set<string>();
  return tasks.map((task) => {
    const base = taskKeyOf(task) || "task";
    let candidate = base;
    let suffix = 2;
    while (used.has(candidate)) candidate = `${base}-${suffix++}`;
    used.add(candidate);
    return candidate;
  });
};

// ----- conditions -----

const rulesFor = (path: WorkflowConditionPath) =>
  path.criteria
    .filter((criterion) => criterion.variable)
    .map((criterion) => ({
      var: criterion.variable,
      cmp: OPERATOR_TO_CMP[criterion.operator],
      val: criterion.value,
    }));

/** A `path` transition's ConditionJson, in WorkflowConditionEvaluator's `{op, rules[]}` shape. */
export const toConditionJson = (path: WorkflowConditionPath) =>
  JSON.stringify({ op: path.logic, rules: rulesFor(path) });

/** The inverse of rulesFor: `{var, cmp, val}` rules (a path's ConditionJson or ConfigJson criteria). */
export const criteriaFromRules = (rules: unknown): WorkflowConditionCriterion[] =>
  asArray(rules).map((raw) => {
    const rule = asObject(raw);
    return {
      ...createWorkflowCriterion(),
      variable: asString(rule.var),
      operator: CMP_TO_OPERATOR[asString(rule.cmp)] ?? "is",
      value: asString(rule.val),
    };
  });

// ----- stage ConfigJson -----

/**
 * A stage's ConfigJson. Routes are written as the target canvas's clientId solely so the server's
 * Validate checklist sees them as routed (`$.gates.accept.route`, `$.paths[*].route`); the
 * transitions are what actually carry them, and loading never reads these values back.
 */
export const toConfigJson = (canvas: WorkflowCanvas): string => {
  switch (canvas.type) {
    case "approval": {
      const { approval } = canvas;
      const reference = approval.approvers.trim();
      const needsReference =
        approval.source === "specific-user" || isFieldPersonSource(approval.source);
      const approverSource = APPROVER_SOURCE_TO_API[approval.source];
      return JSON.stringify({
        // The runtime's keys (80422 WorkflowApprovalConfigDto) …
        countingModel: APPROVAL_MODEL_TO_API[approval.model],
        approverSource,
        approverConfig: {
          userIds: approval.source === "specific-user" ? parseUserIds(reference) : [],
          fieldKey: isFieldPersonSource(approval.source) && reference ? reference : null,
        },
        n: approval.model === "any-n" ? approval.threshold : null,
        percent: approval.model === "percentage" ? approval.threshold : null,
        // The reference exactly as entered. userIds above keeps only numeric ids (what the runtime
        // resolves); this keeps anything else (e.g. an older draft's typed names) from being lost
        // on a save → load round trip. The runtime ignores it.
        approvers: reference,
        // … plus the two the server's Validate checklist reads: `$.approver` is blank exactly when
        // the source still needs its reference (users, or a field), and the gate routes.
        approver: needsReference ? reference : approverSource,
        gates: {
          accept: { route: approval.acceptRouteId },
          reject: { route: approval.rejectRouteId },
          cancel: { route: approval.cancelRouteId },
        },
      });
    }
    case "task": {
      const taskKeys = assignTaskKeys(canvas.tasks);
      return JSON.stringify({
        completionRule: "all",
        tasks: canvas.tasks.map((task, index) => ({
          taskKey: taskKeys[index],
          title: task.title,
          description: task.description,
          assignModel: PERSON_SOURCE_TO_ASSIGN_MODEL[task.assigneeSource],
          assigneeRef: task.assignee || null,
        })),
      });
    }
    case "conditional":
      // The full path list lives here so an unrouted path (which has no transition) survives a
      // save; criteria are repeated in each routed path's ConditionJson for the runtime.
      return JSON.stringify({
        paths: canvas.paths.map((path) => ({
          name: path.name,
          logic: path.logic,
          criteria: rulesFor(path),
          route: path.routeId,
        })),
      });
    case "webservice": {
      const { webService } = canvas;
      // Same shape as Epic 80423's preview request, which is what the runtime stores and replays.
      return JSON.stringify({
        method: webService.method,
        url: webService.url,
        onFailure: webService.onFailure,
        params: webService.params.map(({ key, value }) => ({ key, value })),
        headers: webService.headers.map(({ key, value }) => ({ key, value })),
        responseCaptures: webService.captures.map((capture) => ({
          var: capture.variable,
          path: capture.responseField,
        })),
        // Omitted when empty, so clearing a loaded body removes it (see mergeOverServerJson).
        ...(webService.body?.trim() ? { bodyTemplate: webService.body } : {}),
      });
    }
    case "email": {
      const { email } = canvas;
      const recipient = email.recipient.trim();
      // A typed address is a literal recipient (`to`); anything else is the user id / field key
      // the runtime resolves through toModel (80423 WorkflowEmailStageConfig).
      const isAddress = email.recipientSource === "specific-user" && recipient.includes("@");
      const attachment = email.attachment.trim();
      return JSON.stringify({
        toModel: kebabToCamel(email.recipientSource),
        toRef: !isAddress && recipient ? recipient : null,
        to: isAddress ? [recipient] : [],
        cc: [],
        subject: email.subject,
        bodyHtml: email.body,
        attachments: attachment ? [{ fieldKey: attachment }] : [],
      });
    }
    case "pause":
      // `route` is a PathLabel for the runtime and null means the default gate — which is where the
      // "Then go to" target is wired as a transition.
      return JSON.stringify({
        amount: canvas.pause.amount,
        // Left out when not set: the server's own value (unrecognised or absent) is then kept as it
        // was rather than overwritten (see mergeOverServerJson), until the author picks a unit.
        unit: canvas.pause.unit ? PAUSE_UNIT_TO_API[canvas.pause.unit] : undefined,
        route: null,
      });
    case "cancel":
      // 80423 WorkflowCancelStageConfig: the default note is the engine-cancel reasonTemplate.
      return JSON.stringify({
        reasonTemplate: canvas.cancel.defaultNote || null,
        noteRequired: canvas.cancel.noteRequired,
        notify: [],
      });
    default:
      return "{}";
  }
};

// ----- reading ConfigJson back (runtime keys first, then the pre-runtime ones a draft may hold) -----

export const approvalFromConfig = (
  config: JsonObject,
  defaults: WorkflowApprovalConfig,
): Omit<WorkflowApprovalConfig, "acceptRouteId" | "rejectRouteId" | "cancelRouteId"> => {
  const approverConfig = asObject(config.approverConfig);
  const source =
    API_TO_APPROVER_SOURCE[asString(config.approverSource)] ??
    fromResolverSource(config.source) ??
    defaults.source;
  const model =
    API_TO_APPROVAL_MODEL[asString(config.countingModel)] ??
    approvalModelFromConfig(config.model) ??
    defaults.model;
  const userIds = asArray(approverConfig.userIds).filter((id): id is number => typeof id === "number");
  const fieldKey = asString(approverConfig.fieldKey) || asString(config.fieldKey);
  // The verbatim reference first (lossless); userIds only for a config written without it.
  const approvers =
    source === "specific-user"
      ? asString(config.approvers) || userIds.join(",")
      : isFieldPersonSource(source)
        ? fieldKey || asString(config.approvers)
        : "";
  const threshold =
    model === "any-n"
      ? asNumberOrNull(config.n) ?? asNumberOrNull(config.threshold)
      : model === "percentage"
        ? asNumberOrNull(config.percent) ?? asNumberOrNull(config.threshold)
        : null;
  return { model, threshold, source, approvers };
};

const EMAIL_SOURCES: WorkflowEmailRecipientSource[] = ["specific-user", "dynamic-user", "dynamic-manager"];

export const emailFromConfig = (config: JsonObject): WorkflowEmailConfig => {
  const model = camelToKebab(asString(config.toModel) || asString(config.recipientSource));
  const firstTo = asArray(config.to).find((entry) => typeof entry === "string") as string | undefined;
  const firstAttachment = asObject(asArray(config.attachments)[0]);
  return {
    recipientSource: EMAIL_SOURCES.find((source) => source === model) ?? "specific-user",
    recipient: firstTo ?? (asString(config.toRef) || asString(config.recipient)),
    subject: asString(config.subject),
    body: asString(config.bodyHtml) || asString(config.body),
    attachment: asString(firstAttachment.fieldKey) || asString(config.attachment),
  };
};

export const cancelFromConfig = (config: JsonObject): WorkflowCancelConfig => ({
  noteRequired: config.noteRequired === true,
  defaultNote: asString(config.reasonTemplate) || asString(config.defaultNote),
});
